import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowsClockwise } from "@phosphor-icons/react";
import api, { formatApiError } from "../../lib/api";
import LeaveAnalyticsCard from "../../components/LeaveAnalyticsCard";
import ApproverCoachCard from "../../components/ApproverCoachCard";
import ApprovalsPending from "./ApprovalsPending";
import ApprovalsHistory from "./ApprovalsHistory";
import TeamCalendar from "./TeamCalendar";

/**
 * Team Approvals container: owns the sub-tab state, the pending/history
 * data loading, and all decision callbacks. Presentational bits (rows,
 * mobile cards, history table, calendar grid) live in sibling files so
 * this component stays focused on state + orchestration.
 */
export default function TeamApprovals({ onChange }) {
  const [subtab, setSubtab] = useState("pending"); // "pending" | "history" | "calendar"
  const [items, setItems] = useState([]);
  const [history, setHistory] = useState([]);
  const [calMonth, setCalMonth] = useState(new Date().toISOString().slice(0, 7));
  const [calendar, setCalendar] = useState({ items: [] });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [noteFor, setNoteFor] = useState({});
  const [coverFor, setCoverFor] = useState({});
  const [selected, setSelected] = useState(() => new Set());
  const [confirmFor, setConfirmFor] = useState(null);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    api.get("/hierarchy/employees")
      .then((r) => setEmployees(r.data || []))
      .catch(() => setEmployees([]));
  }, []);

  // Precompute cover picker options per leave to avoid re-filtering the
  // full employee list on every render.
  const coverOptionsByLeave = useMemo(() => {
    const map = {};
    for (const l of items) {
      const suggestedId = l.suggested_cover?.id;
      const leaveDept = l.employee_department || l.department;
      map[l.id] = employees
        .filter((e) => e.id !== l.employee_id)
        .map((e) => ({
          id: e.id,
          label: `${suggestedId === e.id ? "★ " : ""}${e.name}${e.department !== leaveDept ? ` (${e.department})` : ""}${e.level ? ` · ${e.level}` : ""}`,
        }));
    }
    return map;
  }, [items, employees]);

  const load = async () => {
    setLoading(true);
    try {
      const [p, h] = await Promise.all([
        api.get("/hr/approvals/pending").then((r) => r.data).catch(() => []),
        api.get("/hr/approvals/history").then((r) => r.data).catch(() => []),
      ]);
      setItems(Array.isArray(p) ? p : []);
      setHistory(Array.isArray(h) ? h : []);
      const preset = {};
      (Array.isArray(p) ? p : []).forEach((lv) => {
        if (lv.suggested_cover?.id) preset[lv.id] = lv.suggested_cover.id;
      });
      setCoverFor(preset);
      setSelected(new Set());
      setConfirmFor(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load approvals");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (subtab !== "calendar") return;
    api.get(`/hr/approvals/calendar?month=${calMonth}`)
      .then((r) => setCalendar(r.data))
      .catch(() => setCalendar({ items: [] }));
  }, [subtab, calMonth]);

  const toggleSel = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };
  const toggleSelAll = () => {
    setSelected((s) => (s.size === items.length ? new Set() : new Set(items.map((l) => l.id))));
  };

  const decide = async (leave, action, { skipConfirm = false } = {}) => {
    if (action === "approve" && !skipConfirm && leave.conflicts && leave.conflicts.length > 0) {
      setConfirmFor({ id: leave.id, action });
      return;
    }
    setBusyId(leave.id);
    try {
      const payload = { action, note: (noteFor[leave.id] || "").trim() || null };
      if (action === "approve") payload.cover_employee_id = coverFor[leave.id] || null;
      await api.patch(`/hr/approvals/${leave.id}`, payload);
      toast.success(action === "approve" ? "Leave approved" : "Leave rejected");
      setNoteFor((n) => ({ ...n, [leave.id]: "" }));
      setConfirmFor(null);
      await load();
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusyId(null); }
  };

  const bulkDecide = async (action) => {
    if (selected.size === 0) return toast.error("Select at least one row");
    if (!window.confirm(`${action === "approve" ? "Approve" : "Reject"} ${selected.size} leave request(s)?`)) return;
    try {
      const { data } = await api.post("/hr/approvals/bulk", { ids: Array.from(selected), action, note: null });
      const skippedNote = data.skipped_count > 0 ? ` (${data.skipped_count} skipped)` : "";
      toast.success(`${data.applied_count} ${action === "approve" ? "approved" : "rejected"}${skippedNote}`);
      await load();
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/hr/approvals/history.csv", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `approvals-history-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Export failed");
    }
  };

  return (
    <div data-testid="team-approvals">
      <div className="p-3 md:p-5 pb-0">
        <LeaveAnalyticsCard />
      </div>
      {subtab === "pending" && <ApproverCoachCard refreshKey={items.length} onChanged={load} />}
      <div className="border-b border-zinc-950 bg-zinc-50 px-5 py-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500">MY TEAM · APPROVALS</div>
          <div className="text-sm text-zinc-700 mt-0.5">
            Pending items route to you as the assigned Line Manager. Reassignments retarget open items automatically.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-zinc-950">
            <button
              onClick={() => setSubtab("pending")}
              className={`px-3 py-1.5 text-[10px] font-mono tracking-[0.15em] uppercase ${subtab === "pending" ? "bg-zinc-950 text-white" : "text-zinc-700 hover:bg-zinc-100"}`}
              data-testid="approvals-tab-pending"
            >
              Pending · {items.length}
            </button>
            <button
              onClick={() => setSubtab("history")}
              className={`px-3 py-1.5 text-[10px] font-mono tracking-[0.15em] uppercase border-l border-zinc-950 ${subtab === "history" ? "bg-zinc-950 text-white" : "text-zinc-700 hover:bg-zinc-100"}`}
              data-testid="approvals-tab-history"
            >
              History · {history.length}
            </button>
            <button
              onClick={() => setSubtab("calendar")}
              className={`px-3 py-1.5 text-[10px] font-mono tracking-[0.15em] uppercase border-l border-zinc-950 ${subtab === "calendar" ? "bg-zinc-950 text-white" : "text-zinc-700 hover:bg-zinc-100"}`}
              data-testid="approvals-tab-calendar"
            >
              Calendar
            </button>
          </div>
          {subtab === "history" && (
            <button
              onClick={exportCsv}
              className="text-[10px] font-mono tracking-[0.1em] uppercase border border-zinc-950 px-3 py-1.5 hover:bg-zinc-950 hover:text-white"
              data-testid="approvals-csv-btn"
            >
              Export CSV
            </button>
          )}
          <button onClick={load} className="text-xs font-mono text-zinc-500 hover:text-zinc-950 flex items-center gap-1" data-testid="team-approvals-refresh-btn">
            <ArrowsClockwise size={12} /> REFRESH
          </button>
        </div>
      </div>

      {subtab === "pending" && selected.size > 0 && (
        <div className="border-b border-zinc-950 bg-amber-50 px-5 py-2 flex items-center gap-3 text-xs" data-testid="bulk-bar">
          <span className="font-mono">{selected.size} SELECTED</span>
          <button
            onClick={() => bulkDecide("approve")}
            className="text-[10px] font-mono tracking-[0.1em] uppercase border border-emerald-700 text-emerald-700 px-3 py-1 hover:bg-emerald-700 hover:text-white"
            data-testid="bulk-approve-btn"
          >
            Approve selected
          </button>
          <button
            onClick={() => bulkDecide("reject")}
            className="text-[10px] font-mono tracking-[0.1em] uppercase border border-[#D0021B] text-[#D0021B] px-3 py-1 hover:bg-[#D0021B] hover:text-white"
            data-testid="bulk-reject-btn"
          >
            Reject selected
          </button>
          <button onClick={() => setSelected(new Set())} className="text-[10px] font-mono text-zinc-500 hover:text-zinc-950" data-testid="bulk-clear-btn">
            Clear
          </button>
        </div>
      )}

      {loading ? (
        <div className="px-5 py-10 text-center text-xs font-mono text-zinc-400">LOADING…</div>
      ) : subtab === "pending" ? (
        <ApprovalsPending
          items={items}
          employees={employees}
          selected={selected}
          toggleSel={toggleSel}
          toggleSelAll={toggleSelAll}
          noteFor={noteFor}
          setNoteFor={setNoteFor}
          coverFor={coverFor}
          setCoverFor={setCoverFor}
          coverOptionsByLeave={coverOptionsByLeave}
          confirmFor={confirmFor}
          setConfirmFor={setConfirmFor}
          busyId={busyId}
          decide={decide}
        />
      ) : subtab === "history" ? (
        <ApprovalsHistory history={history} />
      ) : (
        <TeamCalendar month={calMonth} onMonthChange={setCalMonth} data={calendar} />
      )}
    </div>
  );
}
